import React, { useState, useRef, useEffect } from "react";
import { Button, Col, Row, Card, Spinner, Input } from "reactstrap";
import { connect } from "react-redux";
import CustomModal from "../../../../../../../Components/Modals/CustomModal";
import FetchProjectAsinSkusListService from "../../../../../../../../Service/DashboardServices/ContentManagementServices/FetchProjectAsinSkusListService";
import fetchProductListingDataService from "../../../../../../../../Service/OPSServices/AmazonProductListing_v1/fetchProductListingDataService";
import patchListingFieldsService from "../../../../../../../../Service/OPSServices/AmazonProductListing_v1/patchListingFieldsService";
import AmazonProductListingUpdatePopup from "../../../../../ProductListingApi_v1/Components/AmazonProductListingUpdatePopup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";

const AmazonListingImageUpdatePopup = (props) => {
    const [isUpdateListingPopupOpen, setIsUpdateListingPopupOpen] = useState(false);
    const [isResolving, setIsResolving] = useState(false);
    const [resolveError, setResolveError] = useState("");

    const [targets, setTargets] = useState([]);
    const [selectedKeys, setSelectedKeys] = useState(new Set());
    const [expandedKey, setExpandedKey] = useState("");

    const [isValidating, setIsValidating] = useState(false);
    const [updatingKey, setUpdatingKey] = useState("");
    const [isBulkUpdating, setIsBulkUpdating] = useState(false);
    const resultsSectionRef = useRef(null);

    const targetKey = (t) => `${t.asin}__${t.sku}`;

    const hasValidPosition = props.position && String(props.position) !== '0';

    const handleUpdateAmazonListing = () => {

        setIsUpdateListingPopupOpen(true);

        if (!hasValidPosition) {
            setResolveError("This asset has no Amazon Position No. set. Please set it before replacing the listing image.");
            setTargets([]);
            setSelectedKeys(new Set());
            return;
        }

        const asinArray = props.asinList
            ? props.asinList.split(",").map(item => item.trim())
            : [];

        setIsResolving(true);
        setResolveError("");
        setTargets([]);
        setSelectedKeys(new Set());

        FetchProjectAsinSkusListService(props.token, {
            integerId: props.integerId,
            asinList: asinArray
        }).then(async (res) => {

            if (!res || res.error) {
                setResolveError("Unable to fetch ASIN/SKU list.");
                setIsResolving(false);
                return;
            }

            const skuData = res.skuData || {};

            const pairs = [];
            asinArray.forEach((asin) => {
                (skuData[asin] || []).forEach((sku) => {
                    pairs.push({ asin, sku });
                });
            });

            if (pairs.length === 0) {
                setResolveError("No SKUs found for the mapped ASIN(s).");
                setIsResolving(false);
                return;
            }

            const res2 = await fetchProductListingDataService(props.token, {
                integerId: props.integerId,
                fetchImagesOnly: true,
                position: props.position,
                pairs: pairs
            }).catch(() => null);

            const resultList = (res2 && res2.targets) || [];

            const resultMap = {};
            resultList.forEach((r) => {
                resultMap[`${r.asin}__${r.sku}`] = r;
            });

            const resolved = pairs.map((pair) => {
                const r = resultMap[`${pair.asin}__${pair.sku}`];
                const isSuccess = !!(r && !r.error && r.attribute);

                return {
                    asin: pair.asin,
                    sku: pair.sku,
                    attribute: isSuccess ? r.attribute : "",
                    currentImageUrl: isSuccess ? (r.currentImageUrl || "") : "",
                    productType: isSuccess ? (r.productType || "") : "",
                    resolveError: isSuccess
                        ? ""
                        : ((r && r.message) || "Unable to resolve this listing's image attribute."),
                    validation: null,
                    updated: false
                };
            });

            setTargets(resolved);

            // default: select every successfully-resolved row
            setSelectedKeys(new Set(
                resolved.filter((t) => !t.resolveError && t.attribute).map((t) => targetKey(t))
            ));

            setIsResolving(false);
        }).catch(() => {
            setResolveError("Unable to fetch ASIN/SKU list.");
            setIsResolving(false);
        });
    };

    const toggleSelected = (key) => {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const toggleSelectAll = () => {
        const selectableKeys = targets
            .filter((t) => !t.resolveError && t.attribute && !t.updated)
            .map((t) => targetKey(t));

        const allCurrentlySelected = selectableKeys.every((k) => selectedKeys.has(k));

        if (allCurrentlySelected) {
            // unselect all
            setSelectedKeys((prev) => {
                const next = new Set(prev);
                selectableKeys.forEach((k) => next.delete(k));
                return next;
            });
        } else {
            // select all
            setSelectedKeys((prev) => new Set([...prev, ...selectableKeys]));
        }
    };

    // Only resolved AND selected rows are candidates for validate/update
    const resolvedTargets = targets.filter(
        (t) => !t.resolveError && t.attribute && selectedKeys.has(targetKey(t))
    );
    const hasValidatedOnce = targets.some((t) => t.validation);

    // ---------- Validate all selected (mode: VALIDATION_PREVIEW) ----------
    const handleValidateAll = async () => {

        if (resolvedTargets.length === 0 || !props.replacementImage || !props.replacementImage.url) return;

        setIsValidating(true);

        const results = await Promise.all(
            resolvedTargets.map((t) =>
                patchListingFieldsService(props.token, {
                    asin: t.asin,
                    sku: t.sku,
                    mode: "VALIDATION_PREVIEW",
                    integerId: props.integerId,
                    productType: t.productType,
                    updates: [{ field: t.attribute, value: props.replacementImage.url }]
                }).then((res) => ({ key: targetKey(t), res }))
            )
        );

        setTargets((prev) =>
            prev.map((t) => {
                const match = results.find((r) => r.key === targetKey(t));
                return match ? { ...t, validation: match.res } : t;
            })
        );

        setIsValidating(false);

        setTimeout(() => {
            resultsSectionRef.current && resultsSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
    };

    // ---------- Update ONE target (mode: LIVE) — same as the existing single-listing flow ----------
    const handleUpdateSingle = async (target) => {

        const key = targetKey(target);
        setUpdatingKey(key);

        const res = await patchListingFieldsService(props.token, {
            asin: target.asin,
            sku: target.sku,
            mode: "LIVE",
            integerId: props.integerId,
            updates: [{ field: target.attribute, value: props.replacementImage.url }],
            moveToAssetGallery: true,
            moveToAssetGalleryData: props.moveToAssetGalleryData
        });

        setTargets((prev) =>
            prev.map((t) =>
                targetKey(t) === key
                    ? { ...t, validation: res, updated: !res.issues || res.issues.length === 0 }
                    : t
            )
        );

        setUpdatingKey("");
    };
    const handleUpdateSelected = async () => {

        const updatableTargets = resolvedTargets.filter((t) => {
            const v = t.validation;
            const isValid = v && v.type !== "WARNING" && (!v.issues || v.issues.length === 0);
            return isValid && !t.updated;
        });

        if (updatableTargets.length === 0) return;

        setIsBulkUpdating(true);

        const results = await Promise.all(
            updatableTargets.map((t) =>
                patchListingFieldsService(props.token, {
                    asin: t.asin,
                    sku: t.sku,
                    mode: "LIVE",
                    integerId: props.integerId,
                    updates: [{ field: t.attribute, value: props.replacementImage.url }],
                    moveToAssetGallery: true,
                    moveToAssetGalleryData: props.moveToAssetGalleryData
                }).then((res) => ({ key: targetKey(t), res }))
            )
        );

        setTargets((prev) =>
            prev.map((t) => {
                const match = results.find((r) => r.key === targetKey(t));
                if (!match) return t;
                return {
                    ...t,
                    validation: match.res,
                    updated: !match.res.issues || match.res.issues.length === 0
                };
            })
        );

        setIsBulkUpdating(false);
    };
    return (
        <>
            <Row>
                <Col md={12}>
                    <p className="mb-4">
                        Please choose the action you would like to perform.
                    </p>
                </Col>

                <Col md={12}>
                    <Row className="d-flex justify-content-between">

                        <Col md={6} className="mb-2 justify-content-center">
                            <Button
                                className="btn-background-color w-75"
                                onClick={() => {
                                    props.setIsMoveAssetsPopup(false);
                                    props.handleMoveToAssetgallery();
                                }}
                            >
                                Move to Asset Gallery
                            </Button>
                        </Col>

                        <Col md={6} className="mb-2 justify-content-center">
                            <Button
                                className="btn-background-color w-75"
                                onClick={handleUpdateAmazonListing}
                            >
                                Update Amazon Listing
                            </Button>
                        </Col>

                    </Row>
                </Col>
                <Col md={12} className='ml-1 mt-3'>
                    <p>⚠ Note: After the Amazon Listing is updated successfully, the asset will be automatically moved to the Asset Gallery.</p>
                </Col>

            </Row>

            <CustomModal
                isOpen={isUpdateListingPopupOpen}
                toggle={() => setIsUpdateListingPopupOpen(false)}
                size="xl"
                className="curve-popup"
                header={`Update Amazon Listing — Position #${props.position || ""}`}
            >
                <Card className="curve-popup p-3 border-0">
                    <Row>

                        <Col md={12} className="mb-3 d-flex align-items-center">
                            <b className="mr-3">Replacement Image:</b>
                            {props.replacementImage && props.replacementImage.url && (
                                <img
                                    src={props.replacementImage.url}
                                    alt="Replacement"
                                    style={{ width: "80px", height: "50px", objectFit: "contain", border: "1px solid #ddd", borderRadius: "4px", background: "#fff" }}
                                />
                            )}
                            <span className="ml-2" style={{ fontSize: "13px" }}>
                                {props.replacementImage && props.replacementImage.fileName}
                            </span>
                        </Col>

                        {isResolving && (
                            <Col md={12} className="text-center my-4">
                                <Spinner color="primary" />
                                <p className="mt-2">Finding all mapped ASIN/SKU listings for position #{props.position}…</p>
                            </Col>
                        )}

                        {!isResolving && resolveError && (
                            <Col md={12} className="my-3">
                                <p className="text-danger">{resolveError}</p>
                            </Col>
                        )}

                        {!isResolving && targets.length > 0 && (
                            <Col md={12}>
                                <h6 className="mb-3">
                                    This will replace <b>Position #{props.position}</b> across {resolvedTargets.length} of {targets.filter(t => !t.resolveError && t.attribute).length} listing(s):
                                </h6>

                                <table className="table table-sm" style={{ fontSize: "13px" }}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: "30px" }}>
                                                <Input
                                                    type="checkbox"
                                                    checked={
                                                        targets.some((t) => !t.resolveError && t.attribute && !t.updated) &&
                                                        targets
                                                            .filter((t) => !t.resolveError && t.attribute && !t.updated)
                                                            .every((t) => selectedKeys.has(targetKey(t)))
                                                    }
                                                    onChange={toggleSelectAll}
                                                    style={{ position: "static", margin: 0 }}
                                                />
                                            </th>
                                            <th>ASIN</th>
                                            <th>SKU</th>
                                            <th>Amazon Attribute</th>
                                            <th>Current Image</th>
                                            {hasValidatedOnce && <th>Status</th>}
                                            {hasValidatedOnce && <th></th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {targets.map((t) => {
                                            const key = targetKey(t);
                                            const v = t.validation;
                                            const isValid = v && v.type !== "WARNING" && (!v.issues || v.issues.length === 0);
                                            const isSelectable = !t.resolveError && t.attribute;
                                            const isChecked = selectedKeys.has(key);
                                            const canUpdateThisRow = isValid && isChecked;
                                            return (
                                                <React.Fragment key={key}>
                                                    <tr style={{ opacity: isSelectable && !isChecked ? 0.5 : 1 }}>
                                                        <td>
                                                            {isSelectable && (
                                                                <Input
                                                                    type="checkbox"
                                                                    checked={isChecked}
                                                                    disabled={t.updated}
                                                                    onChange={() => toggleSelected(key)}
                                                                    style={{ position: "static", margin: 0 }}
                                                                />
                                                            )}
                                                        </td>
                                                        <td>{t.asin}</td>
                                                        <td>{t.sku}</td>
                                                        <td>
                                                            {t.resolveError || !t.attribute
                                                                ? <span className="text-danger">{t.resolveError || "Not resolved"}</span>
                                                                : t.attribute.replace(/_/g, " ")}
                                                        </td>
                                                        <td>
                                                            {t.currentImageUrl ? (
                                                                <img src={t.currentImageUrl} alt="" style={{ width: "40px", height: "30px", objectFit: "contain" }} />
                                                            ) : "—"}
                                                        </td>
                                                        {hasValidatedOnce && (
                                                            <td>
                                                                {t.updated
                                                                    ? <span className="text-success">✅ Updated</span>
                                                                    : v
                                                                        ? (isValid ? <span className="text-success">Valid</span> : <span className="text-danger">Invalid</span>)
                                                                        : (isChecked ? "—" : "Skipped")}
                                                            </td>
                                                        )}
                                                        {hasValidatedOnce && (
                                                            <td>
                                                                {v && (
                                                                    <Button size="sm" color="link" onClick={() => setExpandedKey(expandedKey === key ? "" : key)}>
                                                                        {expandedKey === key ? "Hide" : "Details"}
                                                                    </Button>
                                                                )}
                                                            </td>
                                                        )}
                                                    </tr>
                                                    {expandedKey === key && v && (
                                                        <tr>
                                                            <td colSpan={7}>
                                                                <AmazonProductListingUpdatePopup
                                                                    data={v}
                                                                    onUpdate={() => handleUpdateSingle(t)}
                                                                    isListingValid={canUpdateThisRow}
                                                                    saving={updatingKey === key}
                                                                    mode={v.mode}
                                                                />
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                <div className="d-flex justify-content-between align-items-center mt-3" ref={resultsSectionRef}>

                                    {hasValidatedOnce ? (() => {
                                        const invalidCount = resolvedTargets.filter((t) => {
                                            const v = t.validation;
                                            const isValid = v && v.type !== "WARNING" && (!v.issues || v.issues.length === 0);
                                            return v && !isValid;
                                        }).length;

                                        const validCount = resolvedTargets.filter((t) => {
                                            const v = t.validation;
                                            const isValid = v && v.type !== "WARNING" && (!v.issues || v.issues.length === 0);
                                            return isValid && !t.updated;
                                        }).length;

                                        return invalidCount > 0 ? (
                                            <span className="text-muted" style={{ fontSize: "12px" }}>
                                                <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                                                Only <b className="text-success">{validCount} valid</b> listing(s) will be updated —{" "}
                                                <b className="text-danger">{invalidCount} invalid</b> will be skipped.
                                            </span>
                                        ) : <span />;
                                    })() : <span />}

                                    <div>
                                        <Button
                                            className="btn-background-color mr-2"
                                            disabled={resolvedTargets.length === 0 || isValidating || hasValidatedOnce}
                                            onClick={handleValidateAll}
                                        >
                                            {isValidating ? "Validating…" : hasValidatedOnce ? "Validate" : "Validate"}
                                        </Button>

                                        {hasValidatedOnce && (() => {
                                            const validCount = resolvedTargets.filter((t) => {
                                                const v = t.validation;
                                                const isValid = v && v.type !== "WARNING" && (!v.issues || v.issues.length === 0);
                                                return isValid && !t.updated;
                                            }).length;

                                            return (
                                                <Button
                                                    className="btn-background-color"
                                                    disabled={isBulkUpdating || validCount === 0}
                                                    // onClick={handleUpdateSelected}
                                                >
                                                    {isBulkUpdating ? "Updating…" : `Update Listing(${validCount})`}
                                                </Button>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </Col>
                        )}
                    </Row>
                </Card>
            </CustomModal>
        </>
    );
};

const mapStateToProps = (state) => ({
    token: state.loggedUser.token,
    storeOverview: state.accountOverview.store.storeOverview,
    UserPermission: state.accountOverview.store.StoreUserPermission,
    accessLevel: state.loggedUser.accessLevel,
});

export default connect(mapStateToProps)(AmazonListingImageUpdatePopup);